/* eslint-disable max-len */
import React from 'react';

interface InstagramProps {
    /**
     * A string representing the CSS class to be applied to the InstagramIcon element to override its default styling.
     */
    className?: string;
    /**
     * A string representing the color to be applied.
     */
    color1?: string;
    /**
     * A number or string representing the height of the InstagramIcon element.
     */
    height?: number | string;
    /**
     * The `testId` property represents a unique identifier, usually in the form of a string, assigned to a component for testing purposes.
     */
    testId?: string;
    /**
     * A number or string representing the height of the InstagramIcon element.
     */
    width?: number | string;
}

/**
 * The `Instagram` component is a functional component designed to render a customizable SVG element representing an Icon.
 * It receives several props through the `InstagramProps` interface, allowing for customization of class, color, dimensions, and testing identifier.
 *
 * @param props           The component props.
 * @param props.className A string representing the CSS class to be applied to the InstagramIcon element to override its default styling.
 * @param props.color1    A string representing the color to be applied to the Icon.
 * @param props.height    A number or string representing the height of the InstagramIcon element.
 * @param props.testId    A string representing a unique identifier assigned to the component for testing purposes.
 * @param props.width     A number or string representing the height of the InstagramIcon element.
 * @returns A React element representing the `Check` component with the specified properties.
 *
 * @example
 * ```tsx
 * const InstagramComponent = <Instagram className="myClass" color1="#000" height="20" testId="myTestId" width="20" />;
 * ```
 */
export const Instagram = React.forwardRef<SVGSVGElement, InstagramProps>((props, ref) => {
    const {className, color1, height, testId, width} = props;

    return (
        <svg
            ref={ref}
            className={className}
            data-cy={testId}
            height={height}
            viewBox="0 0 45 45"
            width={width}
            xmlns="http://www.w3.org/2000/svg"
            xmlnsXlink="http://www.w3.org/1999/xlink"
        >
            <circle cx="22.5" cy="22.6567" r="15.2381" stroke={color1} strokeWidth="1.52381" />
            <path clipRule="evenodd" d="M30.0426 17.4123C30.239 17.9174 30.3761 18.5003 30.4135 19.3482C30.4508 20.1992 30.454 20.4704 30.454 22.6368C30.454 24.8033 30.4446 25.0745 30.4073 25.9255C30.3699 26.7734 30.2326 27.3532 30.0364 27.8614C29.6217 28.9337 28.7739 29.7815 27.7015 30.1961C27.1965 30.3925 26.6136 30.5297 25.7657 30.567C24.9147 30.6045 24.6435 30.6138 22.4771 30.6138C20.3106 30.6138 20.0393 30.6045 19.1884 30.567C18.3404 30.5297 17.7607 30.3925 17.2525 30.1961C16.7226 29.9967 16.2394 29.6818 15.8404 29.2765C15.4352 28.8775 15.1204 28.3975 14.9178 27.8614C14.7214 27.3563 14.5841 26.7734 14.5468 25.9255C14.5094 25.0745 14.5 24.8033 14.5 22.6368C14.5 20.4704 14.5094 20.1992 14.5499 19.345C14.5873 18.4971 14.7244 17.9174 14.9208 17.4093C15.1204 16.8793 15.4352 16.3962 15.8404 15.9971C16.2394 15.5887 16.7194 15.277 17.2557 15.0744C17.7607 14.8781 18.3436 14.7409 19.1914 14.7035C20.0425 14.6661 20.3136 14.6567 22.4801 14.6567C24.6466 14.6567 24.9178 14.6661 25.772 14.7067C26.6198 14.744 27.1997 14.8812 27.7077 15.0776C28.2376 15.277 28.7208 15.5919 29.1198 15.9971C29.5282 16.3962 29.8399 16.8762 30.0426 17.4123ZM28.702 27.3436C28.8111 27.063 28.942 26.6422 28.9763 25.8629C29.0137 25.0212 29.0231 24.7719 29.0231 22.6428C29.0231 20.5137 29.0137 20.2612 28.9763 19.4196C28.942 18.6433 28.8111 18.2195 28.702 17.9389C28.5741 17.5929 28.3715 17.2811 28.1065 17.0224C27.8509 16.7574 27.5361 16.5548 27.1901 16.4271C26.9096 16.318 26.4887 16.1871 25.7094 16.1527C24.8708 16.1153 24.6184 16.106 22.4861 16.106C20.3572 16.106 20.1046 16.1153 19.263 16.1527C18.4868 16.1871 18.0628 16.318 17.7823 16.4271C17.4362 16.5548 17.1245 16.7574 16.8659 17.0224C16.5977 17.2781 16.3951 17.5929 16.2673 17.9389C16.1582 18.2195 16.0273 18.6403 15.993 19.4196C15.9555 20.2581 15.9463 20.5137 15.9463 22.6428C15.9463 24.7719 15.9555 25.0244 15.993 25.8661C16.0273 26.6422 16.1582 27.0662 16.2673 27.3467C16.3951 27.6927 16.5977 28.0044 16.8627 28.2632C17.1183 28.5281 17.4332 28.7307 17.7791 28.8586C18.0597 28.9677 18.4805 29.0986 19.2598 29.1328C20.0984 29.1703 20.354 29.1796 22.4831 29.1796C24.6122 29.1796 24.8646 29.1703 25.7063 29.1328C26.4825 29.0986 26.9064 28.9677 27.1869 28.8586C27.8821 28.5905 28.4338 28.0387 28.702 27.3436ZM18.3809 22.6369C18.3809 20.3738 20.217 18.5378 22.4801 18.5378C24.7432 18.5378 26.5792 20.3738 26.5792 22.6369C26.5792 24.9001 24.7432 26.7361 22.4801 26.7361C20.217 26.7361 18.3809 24.9001 18.3809 22.6369ZM19.8209 22.6372C19.8209 24.1055 21.0117 25.2962 22.4799 25.2962C23.9481 25.2962 25.1389 24.1055 25.1389 22.6372C25.1389 21.1689 23.9481 19.9782 22.4799 19.9782C21.0117 19.9782 19.8209 21.1689 19.8209 22.6372ZM26.7411 19.3324C27.2697 19.3324 27.6982 18.9039 27.6982 18.3754C27.6982 17.8468 27.2697 17.4185 26.7411 17.4185C26.2127 17.4185 25.7842 17.8468 25.7842 18.3754C25.7842 18.9039 26.2127 19.3324 26.7411 19.3324Z" fill={color1} fillRule="evenodd" />
        </svg>
    );
});

Instagram.displayName = 'Instagram';
Instagram.defaultProps = {
    className: '',
    color1: '#fff',
    height: 45,
    testId: 'Instagram',
    width: 45
};